import { create } from 'zustand';
import { useOrganizationStore } from '../store/organization.store';
import { collection, getDocs } from 'firebase/firestore';
import { getOrgCollectionPath } from '../constants/paths';

export interface CollectionData {
  id: string;
  displayName: string;
  [key: string]: any;
}

interface CollectionCache {
  // Global collections (shared across all orgs)
  agency: CollectionData[];
  bpm: CollectionData[];
  client: CollectionData[];
  genres: CollectionData[];
  keys: CollectionData[];
  tags: CollectionData[];
  territories: CollectionData[];
  flightingplatform: CollectionData[];
  broadcaster: CollectionData[];
  mediaowner: CollectionData[];
  /** Global studio-org link table. Sourced from the `artists-global`
   *  Firestore collection — the legacy collection name is kept (the app
   *  forked from a project that called this entity "artist", and we
   *  display it as "Studio" in the UI; data layer still uses the old
   *  name). Each entry has `linkedOrgId` pointing at the /organizations
   *  doc representing the studio. */
  studioOrgs: CollectionData[];
  // Org-scoped collections
  composers: CollectionData[];
  creativedirector: CollectionData[];
  producer: CollectionData[];
  sessionartists: CollectionData[];
  studio: CollectionData[];
  backuplocation: CollectionData[];
}

interface CollectionCacheState extends CollectionCache {
  isInitialized: boolean;
  error: string | null;
  currentOrgId: string | null; // Track which org the cache is for
  loadEssentialCollections: (firebase: any) => Promise<void>;
  loadCollectionData: (firebase: any, orgId?: string | null) => Promise<void>;
  loadOrgCollections: (firebase: any, orgId: string) => Promise<void>;
  /** Re-fetch just the broadcaster + mediaowner global collections. Call
   *  after a Global Org Manager create/edit/delete so callers reading from
   *  the cache (chase-list parser, language editor, job delivery checks)
   *  see the change without a full app reload. */
  refreshGlobalEntries: (firebase: any) => Promise<void>;
  clearCache: () => void;
  allTrackNames?: string[];
}

// Define collections based on role
// Global collections are shared across all organizations.
// Each entry is `{ key, path }` so the cache key in the store can differ
// from the Firestore collection name (needed for `artists-global` whose
// hyphen makes it awkward as a JS object key).
type GlobalCollectionDef = { key: keyof CollectionCache; path: string };
const globalCollections: GlobalCollectionDef[] = [
  { key: 'genres',            path: 'genres' },
  { key: 'tags',              path: 'tags' },
  { key: 'keys',              path: 'keys' },
  { key: 'bpm',               path: 'bpm' },
  { key: 'agency',            path: 'agency' },             // Global agency-org links — chase-list AI matcher
  { key: 'client',            path: 'client' },             // Global client/brand normalization
  { key: 'territories',       path: 'territories' },        // Global for license territories
  { key: 'flightingplatform', path: 'flightingplatform' },  // Global for cross-org normalization
  { key: 'broadcaster',       path: 'broadcaster' },        // Global broadcaster-org links — chase-list AI matcher + delivery
  { key: 'mediaowner',        path: 'mediaowner' },         // Global mediaowner-org links — chase-list AI matcher + delivery
  { key: 'studioOrgs',        path: 'artists-global' },     // Global studio-org links (legacy `artists-global` path) — chase-list AI matcher
];

// Organization-scoped collections (private to each org)
const orgScopedCollections = [
  'composers',
  'creativedirector',
  'producer',
  'sessionartists',
  'studio',
  'backuplocation'
];

export const useCollectionCache = create<CollectionCacheState>((set, get) => ({
  // Global collections
  agency: [],
  bpm: [],
  client: [],
  genres: [],
  keys: [],
  tags: [],
  territories: [],
  flightingplatform: [],
  broadcaster: [],
  mediaowner: [],
  studioOrgs: [],
  // Org-scoped collections
  composers: [],
  creativedirector: [],
  producer: [],
  sessionartists: [],
  studio: [],
  backuplocation: [],
  // State
  isInitialized: false,
  error: null,
  currentOrgId: null,
  allTrackNames: [],

  loadEssentialCollections: async (firebase) => {
    // Pre-load all global collections (shared across all organizations)
    // These are: genres, tags, keys, bpm
    try {
      const collectionData: Partial<CollectionCache> = {};

      for (const { key, path } of globalCollections) {
        try {
          const snapshot = await getDocs(collection(firebase.db, path));
          const data = [] as CollectionData[];

          snapshot.docs.forEach((doc: any) => {
            const docData = doc.data();
            data.push({
              id: doc.id,
              displayName: docData.name || docData.label || docData.text || doc.id,
              ...docData
            });
          });

          collectionData[key] = data;
        } catch (error) {
          throw error;
        }
      }

      // Update state with essential collections and mark as initialized for chatbot use
      set((state) => ({
        ...state,
        ...collectionData,
        isInitialized: true,
      }));
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Unknown error loading essential collections' });
    }
  },

  loadCollectionData: async (firebase, orgId?: string | null) => {
    // Load org-scoped collections for the specified organization
    // Global collections (genres, tags, keys, bpm) are loaded via loadEssentialCollections
    try {
      // Use organization-level role instead of global auth role
      const { currentOrgRole, isSuperAdmin } = useOrganizationStore.getState();
      const role = isSuperAdmin ? 'ADMIN' : (currentOrgRole || 'USER');
      // Get orgId from parameter or from organization store
      const currentOrgId = orgId ?? useOrganizationStore.getState().currentOrgId;

      const collectionData: Partial<CollectionCache> = {};

      // Load organization-scoped collections if user has ADMIN/OPERATOR role
      // and an organization is selected
      if (['ADMIN', 'OPERATOR'].includes(role) && currentOrgId) {
        for (const collectionName of orgScopedCollections) {
          try {
            // Get the org-scoped collection path
            const orgCollectionPath = getOrgCollectionPath(currentOrgId, collectionName);
            const snapshot = await getDocs(collection(firebase.db, orgCollectionPath));
            const data = [] as CollectionData[];
            snapshot.docs.forEach((doc: any) => {
              const docData = doc.data();
              data.push({
                id: doc.id,
                displayName: docData.name || docData.label || docData.text || doc.id,
                ...docData
              });
            });
            collectionData[collectionName as keyof CollectionCache] = data;
          } catch (error) {
            // If org collection doesn't exist yet, initialize with empty array
            collectionData[collectionName as keyof CollectionCache] = [];
          }
        }
      }

      // Fetch all track names from audiometa for the current org
      let allTrackNames: string[] = [];
      if (currentOrgId) {
        try {
          const audiometaPath = getOrgCollectionPath(currentOrgId, 'audiometa');
          const audiometaSnapshot = await getDocs(collection(firebase.db, audiometaPath));
          allTrackNames = audiometaSnapshot.docs
            .map((doc: any) => {
              const data = doc.data();
              return data.trackName || data.trackname || '';
            })
            .filter((name: string) => typeof name === 'string' && name.trim().length > 0);
        } catch (err) {
          // Error loading track names - continue with empty array
        }
      }

      set({
        ...collectionData as CollectionCache,
        isInitialized: true,
        error: null,
        currentOrgId: currentOrgId || null,
        allTrackNames,
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      set({ error: errorMessage });
    }
  },

  /**
   * Load only organization-scoped collections for a specific org
   * Used when switching organizations without reloading global data
   */
  loadOrgCollections: async (firebase, orgId: string) => {
    // Clear org-scoped collections before loading new ones
    // Note: Global collections (agency, client, territories, flightingplatform) are NOT cleared
    set({
      composers: [],
      creativedirector: [],
      producer: [],
      sessionartists: [],
      studio: [],
      backuplocation: [],
      allTrackNames: [],
    });

    try {
      // Use organization-level role instead of global auth role
      const { currentOrgRole, isSuperAdmin } = useOrganizationStore.getState();
      const role = isSuperAdmin ? 'ADMIN' : (currentOrgRole || 'USER');
      if (!['ADMIN', 'OPERATOR'].includes(role)) {
        set({ currentOrgId: orgId });
        return;
      }

      const collectionData: Partial<CollectionCache> = {};

      for (const collectionName of orgScopedCollections) {
        try {
          const orgCollectionPath = getOrgCollectionPath(orgId, collectionName);
          const snapshot = await getDocs(collection(firebase.db, orgCollectionPath));
          const data = [] as CollectionData[];
          snapshot.docs.forEach((doc: any) => {
            const docData = doc.data();
            data.push({
              id: doc.id,
              displayName: docData.name || docData.label || docData.text || doc.id,
              ...docData
            });
          });
          collectionData[collectionName as keyof CollectionCache] = data;
        } catch (error) {
          collectionData[collectionName as keyof CollectionCache] = [];
        }
      }

      // Fetch track names for this org
      let allTrackNames: string[] = [];
      try {
        const audiometaPath = getOrgCollectionPath(orgId, 'audiometa');
        const audiometaSnapshot = await getDocs(collection(firebase.db, audiometaPath));
        allTrackNames = audiometaSnapshot.docs
          .map((doc: any) => {
            const data = doc.data();
            return data.trackName || data.trackname || '';
          })
          .filter((name: string) => typeof name === 'string' && name.trim().length > 0);
      } catch (err) {
        // Error loading track names - continue with empty array
      }

      set((state) => ({
        ...state,
        ...collectionData,
        currentOrgId: orgId,
        allTrackNames,
      }));

    } catch (error) {
      // Failed to load org collections
    }
  },

  /**
   * Refresh the org-link global collections (broadcaster, mediaowner,
   * agency, studioOrgs). Cheaper than re-running loadEssentialCollections
   * when only the org-link collections changed (e.g. GOM create/edit/delete).
   */
  refreshGlobalEntries: async (firebase) => {
    try {
      const targets: GlobalCollectionDef[] = [
        { key: 'broadcaster', path: 'broadcaster' },
        { key: 'mediaowner',  path: 'mediaowner' },
        { key: 'agency',      path: 'agency' },
        { key: 'studioOrgs',  path: 'artists-global' },
      ];
      const update: Partial<CollectionCache> = {};
      for (const { key, path } of targets) {
        const snapshot = await getDocs(collection(firebase.db, path));
        update[key] = snapshot.docs.map((doc: any) => {
          const d = doc.data();
          return {
            id: doc.id,
            displayName: d.name || d.label || d.text || doc.id,
            ...d,
          };
        });
      }
      set((state) => ({ ...state, ...update }));
    } catch (err) {
      // best effort — stale data is preferable to a thrown error in the
      // middle of a GOM save flow.
      console.warn('refreshGlobalEntries failed:', err);
    }
  },

  clearCache: () => {
    set({
      // Global collections
      agency: [],
      bpm: [],
      client: [],
      genres: [],
      keys: [],
      tags: [],
      territories: [],
      flightingplatform: [],
      broadcaster: [],
      mediaowner: [],
      studioOrgs: [],
      // Org-scoped collections
      composers: [],
      creativedirector: [],
      producer: [],
      sessionartists: [],
      studio: [],
      backuplocation: [],
      // State
      isInitialized: false,
      error: null,
      currentOrgId: null,
      allTrackNames: [],
    });
  }
}));